import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Custom dropdown that lazy-renders simulation options so Safari/Chrome don't have to
 * paint hundreds of <option> nodes when the control opens.
 */
export default function SimulationSelector({
  simulations = [],
  selectedId,
  onSelect,
  variant = "dark"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const INITIAL_BATCH = 20;
  const BATCH_SIZE = 20;

  const normalizedTerm = searchTerm.trim().toLowerCase();
  const filteredSimulations = useMemo(() => {
    if (!normalizedTerm) return simulations;
    return simulations.filter(sim => {
      const label = (sim.name || sim.id || '').toLowerCase();
      return label.includes(normalizedTerm);
    });
  }, [simulations, normalizedTerm]);

  const selected = simulations.find(sim => sim.id === selectedId);
  const selectedIndex = filteredSimulations.findIndex(sim => sim.id === selectedId);

  const palette = variant === "light"
    ? {
        buttonBg: "#fff",
        buttonBorder: "#d1d5db",
        buttonColor: "#111827",
        dropdownBg: "#fff",
        dropdownBorder: "#d1d5db",
        textColor: "#111827",
        searchBg: "#f9fafb",
        searchBorder: "#d1d5db",
        searchColor: "#111827",
        optionSelectedBg: "#e0e7ff",
        optionHoverBg: "#f3f4f6",
        shadow: "0 8px 20px rgba(15,23,42,0.15)"
      }
    : {
        buttonBg: "#3A3A3A",
        buttonBorder: "#444",
        buttonColor: "#FFF",
        dropdownBg: "#1E1E1E",
        dropdownBorder: "#444",
        textColor: "#FFF",
        searchBg: "#2C2C2C",
        searchBorder: "#444",
        searchColor: "#FFF",
        optionSelectedBg: "#2C2C2C",
        optionHoverBg: "#2C2C2C",
        shadow: "0 4px 12px rgba(0,0,0,0.4)"
      };

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
  };

  // Reset the virtualized window whenever the dropdown opens or filtering changes.
  useEffect(() => {
    if (!isOpen) return;
    const base = Math.min(INITIAL_BATCH, filteredSimulations.length);
    const ensureSelected = selectedIndex >= 0
      ? Math.min(filteredSimulations.length, Math.max(base, selectedIndex + BATCH_SIZE))
      : base;
    setVisibleCount(ensureSelected);
  }, [isOpen, filteredSimulations.length, selectedIndex]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', onDocClick);
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [isOpen]);

  const handleScroll = () => {
    const listEl = listRef.current;
    if (!listEl) return;
    if (listEl.scrollTop + listEl.clientHeight < listEl.scrollHeight - 20) return;
    setVisibleCount(prev => (prev >= filteredSimulations.length ? prev : Math.min(prev + BATCH_SIZE, filteredSimulations.length)));
  };

  const handleSelect = (sim) => {
    onSelect?.(sim);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const node = selectedOptionRef.current;
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, visibleCount, selectedId]);

  const selectedLabel =
    selected?.name ||
    selected?.id ||
    (selectedId ? String(selectedId) : 'Select simulation');

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 260 }}>
      <button
        onClick={toggleOpen}
        style={{
          padding: '5px 10px',
          borderRadius: 4,
          border: `1px solid ${palette.buttonBorder}`,
          background: palette.buttonBg,
          color: palette.buttonColor,
          minWidth: '100%',
          textAlign: 'left'
        }}
      >
        {selectedLabel}
      </button>

      {isOpen && (
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            width: '100%',
            maxHeight: 240,
            overflowY: 'auto',
            background: palette.dropdownBg,
            border: `1px solid ${palette.dropdownBorder}`,
            borderRadius: 6,
            zIndex: 5,
            boxShadow: palette.shadow
          }}
        >
          <div style={{ position: 'sticky', top: 0, background: palette.dropdownBg, padding: '6px 8px', borderBottom: `1px solid ${palette.dropdownBorder}` }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 8, color: '#888' }}>🔍</span>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search simulations"
                style={{
                  width: '100%',
                  padding: '6px 8px 6px 26px',
                  borderRadius: 4,
                  border: `1px solid ${palette.searchBorder}`,
                  background: palette.searchBg,
                  color: palette.searchColor
                }}
              />
            </div>
          </div>

          {filteredSimulations.slice(0, visibleCount).map(sim => {
            const isSelected = sim.id === selectedId;
            return (
              <button
                key={sim.id}
              ref={isSelected ? selectedOptionRef : null}
              onClick={() => handleSelect(sim)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: isSelected ? palette.optionSelectedBg : 'transparent',
                  color: palette.textColor,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {sim.name || sim.id}
              </button>
            );
          })}
          {visibleCount < filteredSimulations.length && (
            <div style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>
              Loading more…
            </div>
          )}
          {!filteredSimulations.length && (
            <div style={{ padding: '8px 12px', color: '#888' }}>No simulations available</div>
          )}
        </div>
      )}
    </div>
  );
}
